
import { useState, useEffect } from "react";
import { baseUrl } from "../../config";
import moment from 'moment';
import axios from "axios";
import {
  Row,
  Col,
  Button,
  Space,
  notification,
  Form,
  Input,
  Select,
  Table,
  Modal,
  DatePicker,
  AutoComplete,
  Card,
  Typography,
  Divider,
  Empty,
  Tooltip,
  Badge,
  Alert,
  Spin,
  Tag,
  Avatar,
  Upload
} from "antd";
import '../../assets/styles/custom_style.css';
import {
  CalendarOutlined,
  DeleteOutlined,
  EditOutlined,
  UserOutlined,
  TeamOutlined,
  PlusOutlined,
  TableOutlined,
  PhoneOutlined,
  MailOutlined,
  SaveOutlined,
  ClearOutlined,
  HeartOutlined,
  UserAddOutlined,
  CameraOutlined,
  WhatsAppOutlined
} from "@ant-design/icons";

const { Item } = Form;
const { Option } = Select;
const { confirm } = Modal;
const { Text } = Typography;

const MAX_PHOTO_MB = 5;

const RELATIONS = [
  ['Father', '👨 Father'], ['Mother', '👩 Mother'], ['Wife', '👰 Wife'], ['Husband', '🤵 Husband'],
  ['Son', '👦 Son'], ['Daughter', '👧 Daughter'], ['Brother', '👨‍🦱 Brother'], ['Sister', '👩‍🦱 Sister'],
  ['Grandfather', '👴 Grandfather'], ['Grandmother', '👵 Grandmother'], ['Grandson', '👶 Grandson'],
  ['Granddaughter', '👶 Granddaughter'], ['Uncle', '👨‍🦳 Uncle'], ['Aunt', '👩‍🦳 Aunt'], ['Nephew', '👦 Nephew'],
  ['Niece', '👧 Niece'], ['Cousin', '👫 Cousin'], ['Father-in-law', 'Father-in-law'],
  ['Mother-in-law', 'Mother-in-law'], ['Son-in-law', 'Son-in-law'], ['Daughter-in-law', 'Daughter-in-law'],
  ['Brother-in-law', 'Brother-in-law'], ['Sister-in-law', 'Sister-in-law'],
];

const RELATION_COLORS = {
  Father: '#722ed1', Mother: '#eb2f96', Wife: '#f759ab', Husband: '#2f54eb', Son: '#1890ff',
  Daughter: '#52c41a', Brother: '#13c2c2', Sister: '#faad14',
};

const calculateAge = (dob) => (dob ? moment().diff(moment(dob), 'years') : null);

// Days until the next birthday (0 = today), or null without a DOB
const daysToBirthday = (dob) => {
  if (!dob) return null;
  const today = moment().startOf('day');
  const next = moment(dob).year(today.year()).startOf('day');
  if (next.isBefore(today)) next.add(1, 'year');
  return next.diff(today, 'days');
};

const FamilyDetails = ({ user_id }) => {
  const [form] = Form.useForm();
  const [recordId, setRecordId] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [optionSuggestion, setOptionSuggestion] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'table', 'form', 'empty'
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Photo: a newly picked file, its preview URL, and whether the saved photo should be removed
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [annivFile, setAnnivFile] = useState(null);
  const [annivPreview, setAnnivPreview] = useState(null);
  const [removeAnnivImg, setRemoveAnnivImg] = useState(false);

  const watchDob = Form.useWatch('m_dob', form);
  const watchPhone = Form.useWatch('m_phone_no', form);
  const watchMarried = Form.useWatch('m_married_status', form);
  const watchAnniv = Form.useWatch('m_anniversary_date', form);

  useEffect(() => {
    setUserData([]);
    setViewMode('empty');
    setLoading(true);

    if (user_id) {
      fetchFamilyData(user_id);
    } else {
      setLoading(false);
    }
  }, [user_id]);

  // Release blob preview URLs
  useEffect(() => () => {
    if (photoPreview && photoPreview.startsWith('blob:')) URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);
  useEffect(() => () => {
    if (annivPreview && annivPreview.startsWith('blob:')) URL.revokeObjectURL(annivPreview);
  }, [annivPreview]);

  const fetchFamilyData = async (user_id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/family/list/${user_id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        }
      });

      if (response.data.status) {
        const familyData = response.data.result || [];
        setUserData(familyData);
        setViewMode(familyData.length > 0 ? 'table' : 'empty');
      } else {
        notification.info({
          message: 'Info',
          description: response.data.message,
          placement: 'topRight'
        });
        setViewMode('empty');
      }
    } catch (error) {
      console.error("API error:", error);
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to fetch family data',
        placement: 'topRight'
      });
      setViewMode('empty');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    if (!user_id) {
      notification.warning({
        message: 'Warning',
        description: "Please save member basic information first before adding family details.",
        placement: 'topRight'
      });
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        let val = values[key];
        if (key === 'm_dob' || key === 'm_anniversary_date') val = val ? moment(val).format('YYYY-MM-DD') : '';
        formData.append(key, val != null ? val : '');
      });
      formData.append('user_id', user_id);

      if (photoFile) {
        formData.append('m_photo', photoFile);
      } else if (removePhoto) {
        formData.append('remove_photo', 'true');
      }
      if (annivFile) {
        formData.append('m_anniversary_img', annivFile);
      } else if (removeAnnivImg) {
        formData.append('remove_anniversary_img', 'true');
      }

      const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
      const response = isUpdateMode
        ? await axios.patch(`${baseUrl}/family/update/${recordId}`, formData, { headers })
        : await axios.post(`${baseUrl}/family/create`, formData, { headers });

      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: isUpdateMode
            ? 'Family member updated successfully!'
            : 'Family member added successfully!',
          placement: 'topRight'
        });

        handleFormReset();
        await fetchFamilyData(user_id);
        setViewMode('table');
      } else {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to save family member',
          placement: 'topRight'
        });
      }
    } catch (error) {
      console.error("API error:", error);
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'An error occurred while saving',
        placement: 'topRight'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record) => {
    form.setFieldsValue({
      m_name: record.m_name || '',
      m_relation_type: record.m_relation_type || undefined,
      m_relation_name: record.m_relation_name || '',
      m_gender: record.m_gender || undefined,
      m_dob: record.m_dob ? moment(record.m_dob) : null,
      m_anniversary_date: record.m_anniversary_date ? moment(record.m_anniversary_date) : null,
      m_age: record.m_age || '',
      m_married_status: record.m_married_status || undefined,
      m_education: record.m_education || '',
      m_occupation: record.m_occupation || '',
      m_current_address: record.m_current_address || '',
      m_phone_no: record.m_phone_no || '',
      m_email: record.m_email || '',
    });

    setPhotoFile(null);
    setPhotoPreview(record.m_photo || null);
    setRemovePhoto(false);
    setAnnivFile(null);
    setAnnivPreview(record.m_anniversary_img || null);
    setRemoveAnnivImg(false);
    setRecordId(record.id);
    setSelectedRecord(record);
    setIsUpdateMode(true);
    setViewMode('form');
  };

  const handleDelete = async (record) => {
    try {
      const response = await axios.delete(`${baseUrl}/family/delete/${record.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: 'Family member deleted successfully!',
          placement: 'topRight'
        });
        await fetchFamilyData(user_id);
      } else {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to delete family member',
          placement: 'topRight'
        });
      }
    } catch (error) {
      console.error("API error:", error);
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to delete family member',
        placement: 'topRight'
      });
    }
  };

  const showDeleteConfirm = (record) => {
    confirm({
      title: 'Delete Family Member',
      content: `Are you sure you want to delete "${record.m_name}"? This action cannot be undone.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(record);
      },
    });
  };

  const handleFormReset = () => {
    form.resetFields();
    setRecordId(null);
    setSelectedRecord(null);
    setIsUpdateMode(false);
    setPhotoFile(null);
    setPhotoPreview(null);
    setRemovePhoto(false);
    setAnnivFile(null);
    setAnnivPreview(null);
    setRemoveAnnivImg(false);
  };

  const handleViewChange = (newView) => {
    if (newView === 'form') {
      handleFormReset();
    }
    setViewMode(newView);
  };

  const isValidImage = (file) => {
    if (!file.type.startsWith('image/')) {
      notification.error({ message: 'Invalid file', description: 'Please select an image (JPG, PNG, WEBP).', placement: 'topRight' });
      return false;
    }
    if (file.size / 1024 / 1024 > MAX_PHOTO_MB) {
      notification.error({ message: 'File too large', description: `Image must be smaller than ${MAX_PHOTO_MB}MB.`, placement: 'topRight' });
      return false;
    }
    return true;
  };

  const handlePhotoSelect = (file) => {
    if (!isValidImage(file)) return Upload.LIST_IGNORE;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setRemovePhoto(false);
    return false; // upload with the form, not immediately
  };

  const handleAnnivSelect = (file) => {
    if (!isValidImage(file)) return Upload.LIST_IGNORE;
    setAnnivFile(file);
    setAnnivPreview(URL.createObjectURL(file));
    setRemoveAnnivImg(false);
    return false;
  };

  const handleAnnivRemove = () => {
    setAnnivFile(null);
    setAnnivPreview(null);
    setRemoveAnnivImg(Boolean(selectedRecord?.m_anniversary_img));
  };

  const handlePhotoRemove = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setRemovePhoto(Boolean(selectedRecord?.m_photo));
  };

  const handleDobChange = (date) => {
    form.setFieldsValue({ m_age: date ? String(calculateAge(date)) : '' });
  };

  const getPanelValue = async (searchText, columnName, tableName) => {
    if (searchText.length < 3) {
      setOptionSuggestion([]);
      return;
    }

    try {
      const response = await axios.get(`${baseUrl}/payments/getSuggestionValue/`, {
        params: { q: searchText, column: columnName, table: tableName },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.status === 200) {
        setOptionSuggestion(response.data.result);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const handleChangeSuggestion = (value, columnName) => {
    form.setFieldsValue({
      [columnName]: value
    });
  };

  const renderBirthdayWish = (record) => {
    if (!record.m_dob || !record.m_phone_no) {
      return (
        <Tooltip title="Add both Date of Birth and Phone Number to send a birthday wish on WhatsApp">
          <Tag style={{ fontSize: 11 }}>Wish off</Tag>
        </Tooltip>
      );
    }
    const days = daysToBirthday(record.m_dob);
    return (
      <Tooltip title={`Birthday poster will be sent on WhatsApp to ${record.m_phone_no}`}>
        <Tag color={days === 0 ? 'magenta' : 'green'} icon={<WhatsAppOutlined />} style={{ fontSize: 11 }}>
          {days === 0 ? 'Birthday today 🎂' : `In ${days} day${days === 1 ? '' : 's'}`}
        </Tag>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: 'Family Member',
      key: 'member',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar
            size={40}
            src={record.m_photo || undefined}
            icon={!record.m_photo && <UserOutlined />}
            style={{ flexShrink: 0, background: record.m_photo ? undefined : '#e2e8f0', color: '#64748b' }}
          />
          <div>
            <Text strong>{record.m_name || '-'}</Text>
            <div style={{ fontSize: 12, color: '#666' }}>
              {record.m_gender ? `${record.m_gender} · ` : ''}Age: {calculateAge(record.m_dob) ?? (record.m_age || 'N/A')}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Relationship',
      key: 'relationship',
      render: (_, record) => (
        <div>
          <Tag color={RELATION_COLORS[record.m_relation_type] || '#666'}>
            {record.m_relation_type || '-'}
          </Tag>
          {record.m_relation_name && (
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              with {record.m_relation_name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Personal Info',
      key: 'personal',
      render: (_, record) => (
        <div>
          {record.m_dob && (
            <div style={{ fontSize: 12 }}>
              <CalendarOutlined /> {moment(record.m_dob).format('DD MMM YYYY')}
            </div>
          )}
          {record.m_married_status && (
            <div style={{ fontSize: 12, marginTop: 2 }}>
              <HeartOutlined /> {record.m_married_status === 'Yes' ? 'Married' : 'Single'}
            </div>
          )}
          {record.m_anniversary_date && (
            <div style={{ fontSize: 12, marginTop: 2 }}>
              💍 {moment(record.m_anniversary_date).format('DD MMM YYYY')}
            </div>
          )}
          {!record.m_dob && !record.m_married_status && !record.m_anniversary_date && '-'}
        </div>
      ),
    },
    {
      title: 'Education & Work',
      key: 'education',
      render: (_, record) => (
        <div>
          {record.m_education && <div style={{ fontSize: 12 }}>📚 {record.m_education}</div>}
          {record.m_occupation && <div style={{ fontSize: 12, marginTop: 2 }}>💼 {record.m_occupation}</div>}
          {!record.m_education && !record.m_occupation && '-'}
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_, record) => (
        <div>
          {record.m_phone_no && <div style={{ fontSize: 12 }}><PhoneOutlined /> {record.m_phone_no}</div>}
          {record.m_email && <div style={{ fontSize: 12, marginTop: 2 }}><MailOutlined /> {record.m_email}</div>}
          {!record.m_phone_no && !record.m_email && '-'}
        </div>
      ),
    },
    {
      title: 'Birthday Wish',
      key: 'wish',
      render: (_, record) => renderBirthdayWish(record),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <div className="button-container">
          <Space size="middle">
            <button className="update-btn" onClick={() => handleEdit(record)}> <EditOutlined /></button>
            <button className="delete-btn" onClick={() => showDeleteConfirm(record)}><DeleteOutlined /></button>
          </Space>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading family details...</div>
      </div>
    );
  }

  if (!user_id) {
    return (
      <Alert
        message="Member Required"
        description="Please save member basic information first before adding family details."
        type="warning"
        showIcon
        style={{ margin: '20px 0' }}
      />
    );
  }

  const cardTitle = viewMode === 'form' ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {isUpdateMode ? <EditOutlined style={{ color: '#7a0000' }} /> : <UserAddOutlined style={{ color: '#7a0000' }} />}
      <span>{isUpdateMode ? `Edit Family Member — ${selectedRecord?.m_name || ''}` : 'Add Family Member'}</span>
    </div>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <TeamOutlined style={{ color: '#7a0000' }} />
      <span>Family Details</span>
      {userData.length > 0 && <Badge count={userData.length} style={{ backgroundColor: '#52c41a' }} />}
    </div>
  );

  const cardExtra = viewMode === 'form' ? (
    userData.length > 0 && (
      <Button
        icon={<TableOutlined />}
        onClick={() => handleViewChange('table')}
        disabled={saving}
        style={{ borderRadius: 8, borderColor: '#7a0000', color: '#7a0000' }}
      >
        View All
      </Button>
    )
  ) : (
    userData.length > 0 && (
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => handleViewChange('form')}
        style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8 }}
      >
        Add Member
      </Button>
    )
  );

  const showBirthdayHint = Boolean(watchDob && watchPhone);

  return (
    <Card
      size="small"
      title={cardTitle}
      extra={cardExtra}
      bodyStyle={{ padding: viewMode === 'form' ? '8px 14px 12px' : '8px 12px' }}
    >
      {viewMode === 'empty' && userData.length === 0 && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div>
              <Text type="secondary">No family members found</Text>
              <div style={{ marginTop: 8 }}>
                <Button
                  type="primary"
                  icon={<UserAddOutlined />}
                  onClick={() => handleViewChange('form')}
                  style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8 }}
                >
                  Add First Family Member
                </Button>
              </div>
            </div>
          }
        />
      )}

      {viewMode === 'table' && userData.length > 0 && (
        <Table
          columns={columns}
          dataSource={userData}
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} family members`
          }}
          scroll={{ x: true }}
          rowKey="id"
        />
      )}

      {viewMode === 'form' && (
        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          size="small"
          className="add-payment-form compact-form"
        >
          <Row gutter={[16, 0]}>
            {/* Photo */}
            <Col xs={24} md={5} lg={4} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#64748b', marginBottom: 6 }}>PHOTO</div>
              <Avatar
                size={110}
                src={photoPreview || undefined}
                icon={!photoPreview && <UserOutlined />}
                style={{ border: '3px solid #7a0000', background: photoPreview ? '#fff' : '#e2e8f0', color: '#94a3b8' }}
              />
              <Space direction="vertical" size={4} style={{ marginTop: 10, width: '100%', alignItems: 'center' }}>
                <Upload accept="image/*" showUploadList={false} beforeUpload={handlePhotoSelect}>
                  <Button
                    type="primary"
                    icon={<CameraOutlined />}
                    style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8 }}
                  >
                    {photoPreview ? 'Change' : 'Upload'}
                  </Button>
                </Upload>
                {photoPreview && (
                  <Button type="link" danger size="small" onClick={handlePhotoRemove} style={{ padding: 0 }}>
                    Remove photo
                  </Button>
                )}
                <Text type="secondary" style={{ fontSize: 11, textAlign: 'center' }}>
                  Used on the birthday poster
                </Text>
              </Space>
            </Col>

            {/* Fields */}
            <Col xs={24} md={19} lg={20}>
              <Row gutter={[10, 0]}>
                <Col xs={24}>
                  <Divider orientation="left" style={{ margin: '4px 0' }}>
                    <UserOutlined /> Basic Information
                  </Divider>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Item
                    label="Full Name"
                    name="m_name"
                    rules={[{ required: true, message: 'Please enter full name' }]}
                  >
                    <Input placeholder="Enter full name" prefix={<UserOutlined />} />
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Item
                    label="Relation"
                    name="m_relation_type"
                    rules={[{ required: true, message: 'Please select relation' }]}
                  >
                    <Select placeholder="Select relation" showSearch optionFilterProp="children">
                      {RELATIONS.map(([value, label]) => (
                        <Option key={value} value={value}>{label}</Option>
                      ))}
                    </Select>
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Item label="Relation with" name="m_relation_name">
                    <Input placeholder="Relation with whom" />
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Item
                    label="Gender"
                    name="m_gender"
                    rules={[{ required: true, message: 'Please select gender' }]}
                  >
                    <Select placeholder="Select gender">
                      <Option value="Male">👨 Male</Option>
                      <Option value="Female">👩 Female</Option>
                      <Option value="Other">⚧ Other</Option>
                    </Select>
                  </Item>
                </Col>

                <Col xs={24}>
                  <Divider orientation="left" style={{ margin: '4px 0' }}>
                    <CalendarOutlined /> Personal Details
                  </Divider>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Item label="Date of Birth" name="m_dob">
                    <DatePicker
                      className="filter_dob"
                      format="DD-MM-YYYY"
                      placeholder="Select Date of Birth"
                      style={{ width: '100%' }}
                      onChange={handleDobChange}
                      disabledDate={(d) => d && d.isAfter(moment(), 'day')}
                    />
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Item label="Age" name="m_age" tooltip="Filled automatically from Date of Birth">
                    <Input type="number" min={0} max={150} placeholder="Enter age" />
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Item label="Marital Status" name="m_married_status">
                    <Select placeholder="Select marital status" allowClear>
                      <Option value="Yes">💍 Married</Option>
                      <Option value="No">🎯 Single</Option>
                    </Select>
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Item label="Education" name="m_education">
                    <AutoComplete
                      placeholder="Enter education"
                      options={optionSuggestion}
                      onChange={(value) => handleChangeSuggestion(value, "m_education")}
                      onSearch={(text) => getPanelValue(text, "m_education", "sn_family_members")}
                    />
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Item label="Occupation" name="m_occupation">
                    <AutoComplete
                      placeholder="Enter occupation"
                      options={optionSuggestion}
                      onChange={(value) => handleChangeSuggestion(value, "m_occupation")}
                      onSearch={(text) => getPanelValue(text, "m_occupation", "sn_family_members")}
                    />
                  </Item>
                </Col>

                {(watchMarried === 'Yes' || watchAnniv || annivPreview) && (
                  <>
                    <Col xs={24} sm={12} lg={6}>
                      <Item label="Anniversary Date" name="m_anniversary_date" tooltip="The anniversary image is sent on WhatsApp on this date every year">
                        <DatePicker
                          className="filter_dob"
                          format="DD-MM-YYYY"
                          placeholder="Select Anniversary Date"
                          style={{ width: '100%' }}
                          disabledDate={(d) => d && d.isAfter(moment(), 'day')}
                        />
                      </Item>
                    </Col>

                    <Col xs={24} sm={12} lg={12}>
                      <Item label="Anniversary Image">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 32 }}>
                          {annivPreview ? (
                            <Avatar shape="square" size={40} src={annivPreview} style={{ borderRadius: 6 }} />
                          ) : (
                            <div style={{ width: 40, height: 40, borderRadius: 6, border: '1px dashed #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>💍</div>
                          )}
                          <Upload accept="image/*" showUploadList={false} beforeUpload={handleAnnivSelect}>
                            <Button icon={<CameraOutlined />}>{annivPreview ? 'Change' : 'Upload'}</Button>
                          </Upload>
                          {annivPreview && (
                            <Button type="text" danger icon={<DeleteOutlined />} onClick={handleAnnivRemove} />
                          )}
                          <Text type="secondary" style={{ fontSize: 11 }}>Sent on WhatsApp on the anniversary</Text>
                        </div>
                      </Item>
                    </Col>
                  </>
                )}

                <Col xs={24}>
                  <Divider orientation="left" style={{ margin: '4px 0' }}>
                    <PhoneOutlined /> Contact Information
                  </Divider>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Item
                    label="Phone Number (WhatsApp)"
                    name="m_phone_no"
                    rules={[
                      {
                        pattern: /^\d{10,12}$/,
                        message: 'Please enter a valid phone number (10-12 digits)',
                      }
                    ]}
                  >
                    <Input placeholder="Enter phone number" prefix={<PhoneOutlined />} maxLength={12} />
                  </Item>
                </Col>

                <Col xs={24} sm={12} lg={6}>
                  <Item
                    label="Email"
                    name="m_email"
                    rules={[
                      {
                        type: 'email',
                        message: 'Please enter a valid email address',
                      }
                    ]}
                  >
                    <Input placeholder="Enter email address" prefix={<MailOutlined />} />
                  </Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Item label="Current Address" name="m_current_address">
                    <Input.TextArea placeholder="Enter current address" autoSize={{ minRows: 1, maxRows: 3 }} />
                  </Item>
                </Col>

                <Col xs={24}>
                  <Alert
                    type={showBirthdayHint ? 'success' : 'info'}
                    showIcon
                    icon={<WhatsAppOutlined />}
                    style={{ fontSize: 12, padding: '4px 10px', marginTop: 2 }}
                    message={showBirthdayHint
                      ? `On their birthday (${moment(watchDob).format('DD MMM')}), the same birthday poster sent to members will go to ${watchPhone} on WhatsApp.`
                      : 'Add Date of Birth and Phone Number to send this family member a birthday wish on WhatsApp.'}
                  />
                </Col>
              </Row>
            </Col>

            {/* Action Buttons */}
            <Col xs={24}>
              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10, marginTop: 10, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button icon={<ClearOutlined />} onClick={handleFormReset} disabled={saving} style={{ borderRadius: 8 }}>
                  Clear Form
                </Button>
                <Button type="primary" icon={saving ? null : <SaveOutlined />} loading={saving} onClick={() => form.submit()} style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8, minWidth: 130 }}>
                  {saving ? 'Saving...' : (isUpdateMode ? 'Update Member' : 'Add Member')}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      )}
    </Card>
  );
};

export default FamilyDetails;
